import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@supabase/supabase-js';
import { TCHA_LOGO_BASE64 } from './logo';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

const COLORS = {
  red: '#c8102e',
  redDark: '#a10d25',
  gray: '#808080',
  grayLight: '#f3f4f6',
  grayDark: '#374151',
  text: '#1f2937',
  white: '#ffffff',
};

function formatCurrency(amount) {
  return '$' + (amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function InvoiceList({ onBack, onEditInvoice }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('TCHA_invoices')
        .select('*')
        .order('invoice_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (err) throw err;
      setInvoices(data || []);
    } catch (err) {
      console.error('Error loading invoices:', err);
      setError('Could not load invoices. Check database connection.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  const filteredInvoices = invoices.filter(inv => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      (inv.bill_to_name || '').toLowerCase().includes(q) ||
      (inv.invoice_number || '').toLowerCase().includes(q) ||
      (inv.service_category || '').toLowerCase().includes(q)
    );
  });

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.headerRow}>
          {onBack && (
            <button onClick={onBack} style={styles.backButton}>← Dashboard</button>
          )}
        </div>

        <div style={styles.header}>
          <img src={TCHA_LOGO_BASE64} alt="TCHA Logo" style={styles.logo} />
          <div>
            <h1 style={styles.title}>Past Invoices</h1>
            <p style={styles.subtitle}>Select an invoice to view or correct it</p>
          </div>
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder="Search by client name, invoice number, or service..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={styles.searchInput}
        />

        {error && <div style={styles.errorBox}>{error}</div>}

        {loading && (
          <div style={styles.emptyState}>Loading invoices...</div>
        )}

        {!loading && !error && filteredInvoices.length === 0 && (
          <div style={styles.emptyState}>
            {invoices.length === 0 ? 'No invoices found yet.' : 'No invoices match your search.'}
          </div>
        )}

        {!loading && filteredInvoices.length > 0 && (
          <div style={styles.list}>
            {filteredInvoices.map(inv => (
              <button
                key={inv.id}
                onClick={() => onEditInvoice(inv.id)}
                style={styles.invoiceCard}
              >
                <div style={styles.invoiceCardTop}>
                  <span style={styles.invoiceNumber}>#{inv.invoice_number}</span>
                  <span style={styles.invoiceDate}>{formatDate(inv.invoice_date)}</span>
                </div>
                <div style={styles.invoiceCardMiddle}>
                  <span style={styles.clientName}>{inv.bill_to_name || 'Unnamed client'}</span>
                  <span style={styles.invoiceTotal}>{formatCurrency(inv.total_amount)}</span>
                </div>
                <div style={styles.invoiceCardBottom}>
                  <span style={styles.serviceTag}>{inv.service_category || 'Service'}</span>
                  {parseFloat(inv.balance) > 0 ? (
                    <span style={styles.balanceDue}>{formatCurrency(inv.balance)} due</span>
                  ) : (
                    <span style={styles.balancePaid}>Paid in full</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: `linear-gradient(to bottom right, ${COLORS.grayLight}, #e5e7eb)`,
    padding: '1rem',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  },
  container: {
    maxWidth: '700px',
    margin: '0 auto',
  },
  headerRow: {
    marginBottom: '1rem',
  },
  backButton: {
    padding: '0.5rem 0.875rem',
    background: COLORS.white,
    color: COLORS.red,
    border: '1px solid #e5e7eb',
    borderRadius: '0.5rem',
    fontWeight: 600,
    fontSize: '0.875rem',
    cursor: 'pointer',
    boxShadow: '0 2px 6px rgba(0,0,0,0.06)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    marginBottom: '1.25rem',
    padding: '1.25rem',
    background: COLORS.white,
    borderRadius: '0.75rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  },
  logo: {
    width: '48px',
    height: '48px',
    objectFit: 'contain',
    flexShrink: 0,
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 700,
    color: COLORS.text,
    margin: 0,
    lineHeight: 1.3,
  },
  subtitle: {
    fontSize: '0.8125rem',
    color: COLORS.gray,
    margin: '0.25rem 0 0 0',
  },
  searchInput: {
    width: '100%',
    padding: '0.75rem 1rem',
    border: '1px solid #d1d5db',
    borderRadius: '0.625rem',
    fontSize: '0.9375rem',
    marginBottom: '1.25rem',
    background: COLORS.white,
    boxSizing: 'border-box',
  },
  errorBox: {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '0.875rem 1rem',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
    fontSize: '0.875rem',
    border: '1px solid #fca5a5',
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem 1rem',
    color: COLORS.gray,
    background: COLORS.white,
    borderRadius: '0.75rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },
  invoiceCard: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    background: COLORS.white,
    border: 'none',
    borderLeft: `4px solid ${COLORS.red}`,
    borderRadius: '0.625rem',
    padding: '1rem 1.25rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  invoiceCardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '0.375rem',
  },
  invoiceNumber: {
    fontSize: '0.8125rem',
    fontWeight: 700,
    color: COLORS.gray,
  },
  invoiceDate: {
    fontSize: '0.8125rem',
    color: COLORS.gray,
  },
  invoiceCardMiddle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: '0.5rem',
  },
  clientName: {
    fontSize: '1.0625rem',
    fontWeight: 700,
    color: COLORS.text,
  },
  invoiceTotal: {
    fontSize: '1.0625rem',
    fontWeight: 800,
    color: COLORS.text,
  },
  invoiceCardBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  serviceTag: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: COLORS.grayDark,
    background: COLORS.grayLight,
    padding: '0.25rem 0.625rem',
    borderRadius: '999px',
  },
  balanceDue: {
    fontSize: '0.8125rem',
    fontWeight: 700,
    color: COLORS.red,
  },
  balancePaid: {
    fontSize: '0.8125rem',
    fontWeight: 600,
    color: '#166534',
  },
};
